import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Award, Users, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const Home = () => {
  const handleLinkClick = () => {
    window.scrollTo(0, 0);
  };
  const services = [
    {
      title: "Puntualità e Serietà",
      description: "L'azienda offre ai propri clienti un servizio attento, curato e personalizzato per soddisfare ogni esigenza.",
      icon: <Clock className="h-8 w-8 text-blue-600" />
    },
    {
      title: "Consulenza e Progettazione",
      description: "Collaboriamo con la massima serietà e riservatezza alla progettazione dei vostri campioni.",
      icon: <Users className="h-8 w-8 text-blue-600" />
    },
    {
      title: "Preventivi Gratuiti",
      description: "Contattaci subito per richiedere un preventivo gratuito per i tuoi progetti.",
      icon: <Award className="h-8 w-8 text-blue-600" />
    }
  ];

  const features = [
    "Oltre 30 anni di esperienza",
    "Fustelle per calzature e pelletteria",
    "Settore automobilistico",
    "Sistema CAD-CAM avanzato",
    "Qualità artigianale",
    "Tempi di consegna rapidi"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
            style={{ minWidth: '100%', minHeight: '100%' }}
          >
            <source src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/d7zdk23q_video%20home.mp4" type="video/mp4" />
            {/* Fallback image in case video doesn't load */}
            <img
              src="https://images.unsplash.com/photo-1647427060118-4911c9821b82"
              alt="Industrial machinery"
              className="w-full h-full object-cover"
            />
          </video>
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-6xl md:text-8xl font-bold text-blue-500 mb-4">
              Fustellificio
            </h1>
            <h2 className="text-5xl md:text-7xl font-bold text-blue-400 italic">
              Ideal
            </h2>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Da oltre 30 anni
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-2xl mx-auto">
            produciamo artigianalmente fustelle per calzaturifici, pelletterie e settore automobilistico
          </p>
          <div className="space-x-4">
            <Button size="lg" asChild>
              <Link to="/contatti" onClick={handleLinkClick}>
                Richiedi Preventivo <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild className="bg-transparent border-white text-white hover:bg-white hover:text-gray-900">
              <Link to="/azienda" onClick={handleLinkClick}>
                Scopri di Più
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Company Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">AZIENDA</h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Il Fustellificio Ideal s.n.c è un'azienda leader con oltre 30 anni di esperienza 
                nel settore della produzione di fustelle a nastro; in particolare per le calzature, 
                la pelletteria, l'abbigliamento, le guarnizioni e nel settore automobilistico 
                (sedili e allestimento interno).
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                La conservazione dei metodi artigianali accoppiata con l'utilizzo di macchinari 
                tecnologicamente avanzati quali CAD-CAM ci ha permesso di posizionarci all'apice 
                del settore.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
              <Button size="lg" asChild>
                <Link to="/azienda" onClick={handleLinkClick}>
                  Scopri la Nostra Storia <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_modern-ideal/artifacts/naddyu57_IMG_2211.jpeg"
                alt="Laboratorio Fustellificio Ideal"
                className="w-full h-96 object-cover rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">SERVIZI</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Offriamo un servizio completo e personalizzato per soddisfare ogni esigenza
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8 text-center">
                  <div className="mb-6 flex justify-center">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Pronto per il tuo prossimo progetto?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Contattaci oggi per un preventivo gratuito e personalizzato
          </p>
          <div className="space-x-4">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/contatti" onClick={handleLinkClick}>
                Contattaci Ora <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="bg-transparent border-white text-white hover:bg-white hover:text-blue-600">
              <Link to="/fustelle" onClick={handleLinkClick}>
                Vedi Prodotti
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;